/**
 * Zustand chat muse store.
 */
import type { SseCanvasEntity, SseReelBusiness } from '@/pages/GenerationMedia/ai-chat-reel/components/chatCanvas/types/sseCanvasPayload';
import type { UploadFile } from 'antd';
import { customAlphabet } from 'nanoid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type ReelExecutionSource = 'chat' | 'canvas';
export type EntityCountSource = 'entity' | 'session-query' | null;
export type EntityModalSource = 'entity' | 'canvas' | null;
export type ReelModel = 'standard' | 'director';

export const normalizeReelModel = (value: unknown): ReelModel => (value === 'director' || (Array.isArray(value) && value.includes('director')) ? 'director' : 'standard');

export type ReelExecutionMeta = {
  nodeId?: string;
  actionKind?: string;
};

export type ReelExecutionState = {
  isRunning: boolean;
  source: ReelExecutionSource | null;
  clientRequestId: string | null;
  meta: ReelExecutionMeta | null;
};

export const EMPTY_REEL_EXECUTION: ReelExecutionState = {
  isRunning: false,
  source: null,
  clientRequestId: null,
  meta: null,
};

export type BeginReelExecutionOptions = {
  source: ReelExecutionSource;
  meta?: ReelExecutionMeta;
  /** 续提交：校验已占位的 clientRequestId，不重新占位 */
  clientRequestId?: string;
};

const REEL_EXECUTION_REQUEST_ID_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const createReelExecutionRequestId = customAlphabet(REEL_EXECUTION_REQUEST_ID_ALPHABET, 21);

export interface BrandItem {
  id: string;
  brand_name: string;
}

export type MuseMediaType = 'image' | 'video' | 'audio';

export interface MuseAssetDetail {
  prompt: string;
  prompt_show: string;
  aspect_ratio: string;
  resolution: string;
}

export interface MuseMediaAsset {
  type: string;
  isAssetRegistered?: boolean;
  parent_asset_ids: string[];
  group_id: string[];
  asset_id: string;
  url: string;
  http_url: string;
  thumbnail_url: string;
  thumbnail_http_url: string;
  input_mask_image?: string;
  asset_detail: MuseAssetDetail;
}

export interface MuseWorkbenchAssetItem {
  medio_asset: MuseMediaAsset[];
}

export interface MuseWorkbenchNodeCanvas {
  node_id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  measured: boolean;
  auto_layout: boolean;
  locked: boolean;
  layout_status: MuseWorkbenchNodeLayoutStatus;
}

export type MuseWorkbenchNodeLayoutStatus = 'pending' | 'placed' | 'manual' | 'restored';

export interface MuseWorkbenchNodeRelation {
  parent_node_ids: string[];
  child_node_ids: string[];
  unresolved_parent_asset_ids: string[];
}

export interface MuseWorkbenchNodeSource {
  kind: 'sse' | 'archive' | 'upscale';
  task_id: string;
  step: number;
  message: string;
  request_id: string;
  record_id: string;
  operation: '' | 'upscale_x2' | 'upscale_x4';
  derived_from_node_id: string;
  derived_from_asset_id: string;
}

export interface MuseAnnotationStroke {
  points: number[];
  stroke: string;
  strokeWidth: number;
}

export interface MuseAnnotationText {
  id?: string;
  x: number;
  y: number;
  width?: number;
  text: string;
  fontSize: number;
  fill: string;
}

export interface MuseAnnotationData {
  strokes: MuseAnnotationStroke[];
  texts: MuseAnnotationText[];
}

export interface MuseWorkbenchNode extends MuseWorkbenchAssetItem {
  canvas: MuseWorkbenchNodeCanvas;
  relation: MuseWorkbenchNodeRelation;
  source: MuseWorkbenchNodeSource;
  annotations: MuseAnnotationData;
}

export interface ChatInputMaskAsset {
  id: string;
  url: string;
  http_url: string;
  thumbnail_url: string;
  thumbnail_http_url: string;
}

export type ChatInputMediaFile = Omit<UploadFile, 'url' | 'thumbUrl'> & {
  id: string;
  media_type: MuseMediaType;
  source: 'upload' | 'workbench';
  width: number | null;
  height: number | null;
  /** 后端消费的资源地址，通常是 gs:// 地址。 */
  url: string;
  isAssetRegistered?: boolean;
  /** 预览原图使用的可访问地址。 */
  http_url: string;
  thumbnail_url: string;
  /** 输入框和消息气泡默认展示的缩略图地址。 */
  thumbnail_http_url: string;
  items?: MuseWorkbenchAssetItem;
  node_id?: string;
  asset_id?: string;
  operation_info?: any;
  input_mask_image?: string;
  mask_asset?: ChatInputMaskAsset;
};

export type MuseModelOptionConfig<T extends string | number | boolean> = {
  default: T;
  options: {
    label: string;
    value: T;
    disabled?: boolean;
  }[];
};

export type MuseDurationRange = {
  min: number;
  max: number;
  default: number;
  step: number;
};

export type MuseDurationConfig =
  | {
      default: number;
      options: MuseDurationRange;
    }
  | MuseDurationRange;

export type MuseModelItem = {
  id: string;
  show_name: string;
  video_model?: string;
  image_model?: string;
  supported_resolutions?: string[];
  supported_durations?: MuseDurationRange;
  supported_audio?: boolean;
  supported_image_usage?: string[];
  support_video_edit?: boolean;
  support_video_extension?: boolean;
  description?: string;
  disabled?: boolean;
};

export type MuseModelGroup = {
  key: string;
  label: string;
  items: MuseModelItem[];
};

export type MuseModelDefault = {
  model_mode?: 'auto_assign' | 'manual_assign';
  video_model?: string;
  image_model?: string;
};

export type MuseModelList = {
  image_usages: MuseModelOptionConfig<string>;
  aspect_ratios: MuseModelOptionConfig<string>;
  resolutions: MuseModelOptionConfig<string>;
  durations: MuseDurationConfig;
  audio: MuseModelOptionConfig<boolean>;
  models: {
    default: MuseModelDefault;
    groups: MuseModelGroup[];
  };
};
export type ChatReelSelectionDraft = {
  modelByGroup: Record<string, string>;
  activeModelId?: string;
  imageUsage?: string;
  aspectRatio?: string;
  resolution?: string;
  duration?: number;
  audio?: boolean;
};
export type ChatReelYoutubeUrlType = '' | 'youtube' | 'upload';

export interface ChatReelYoutubeUrl {
  url?: string;
  type: ChatReelYoutubeUrlType;
  http_url?: string;
}

export const EMPTY_CHAT_REEL_YOUTUBE_URL: ChatReelYoutubeUrl = {
  url: '',
  type: '',
};

export const OMNI_VIDEO_EDIT_ACTIONS = ['omni_operate_full_video', 'omni_operate_split_video', 'omni_operate_video', 'reel_quick_video_agent', 'video_edit', 'video_extension'] as const;
export type OmniVideoEditAction = (typeof OMNI_VIDEO_EDIT_ACTIONS)[number];
export const isOmniVideoEditAction = (action?: string): action is OmniVideoEditAction => (OMNI_VIDEO_EDIT_ACTIONS as readonly string[]).includes(action ?? '');

export type GenerateVideoData = {
  action: 'reference_generate_video_single' | 'split_generate_video_single' | 'regenerate_quick_video_single' | OmniVideoEditAction | `regenerate_${string}`;
  entities: SseCanvasEntity[];
  /** 视频编辑入口本次确认的参数快照；发送时优先于会话区的全局模型配置。 */
  reel_business?: SseReelBusiness;
};

const pickNonEmptyId = (...values: (string | undefined | null)[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
};

const pickChatInputMediaFromGenerateVideoData = (generateVideoData: GenerateVideoData | null | undefined, mediaType: MuseMediaType): ChatInputMediaFile[] | undefined => {
  const entity = generateVideoData?.entities?.[0];
  const assets = entity?.reel_business?.assets;
  const files = assets?.[mediaType];
  if (!Array.isArray(files) || files.length === 0) return undefined;

  return files.map((raw, index) => {
    const file = raw as Partial<ChatInputMediaFile>;
    const stableKey = pickNonEmptyId(file.uid, file.id, file.asset_id, file.url) ?? `reel-ref-${mediaType}-${index}`;
    const businessId = pickNonEmptyId(file.id, file.asset_id) ?? stableKey;

    return {
      ...file,
      uid: stableKey,
      id: businessId,
      asset_id: pickNonEmptyId(file.asset_id) ?? '',
      operation_info: file.operation_info ?? entity?.operation_info ?? (entity?.operation_id && entity.operation_type ? { operation_id: entity.operation_id, operation_type: entity.operation_type } : undefined),
      name: file.name?.trim() || pickNonEmptyId(file.asset_id) || `${mediaType}-${index + 1}`,
      media_type: mediaType,
      type: file.type || `${mediaType}/*`,
      source: file.source ?? 'workbench',
      status: file.status ?? 'done',
      width: file.width ?? null,
      height: file.height ?? null,
    } as ChatInputMediaFile;
  });
};

type SetChatInputMediaFileList = (fileList: ChatInputMediaFile[] | ((prev: ChatInputMediaFile[]) => ChatInputMediaFile[])) => void;

type ChatReelState = {
  seessionId: string | null;
  setSeessionId: (seessionId: string) => void;
  reelExecution: ReelExecutionState;
  beginReelExecution: (opts: BeginReelExecutionOptions) => string | null;
  endReelExecution: (clientRequestId?: string | null) => void;
  selectBrand: BrandItem | null;
  setSelectBrand: (selectBrand: BrandItem) => void;
  reelGlobaldisabled: boolean;
  setReelGlobaldisabled: (reelGlobaldisabled: boolean) => void;
  entityCount: number;
  entityCountSource: EntityCountSource;
  setEntityCount: (entityCount: number, source?: Exclude<EntityCountSource, null>) => void;
  entityModalOpen: boolean;
  entityModalSource: EntityModalSource;
  setEntityModalOpen: (entityModalOpen: boolean, source?: Exclude<EntityModalSource, null>) => void;
  entityFormModalOpen: boolean;
  entityFormModalSource: EntityModalSource;
  setEntityFormModalOpen: (entityFormModalOpen: boolean, source?: Exclude<EntityModalSource, null>) => void;
  resetPageStatus: () => void;
  chatInputFileList: ChatInputMediaFile[];
  setChatInputFileList: SetChatInputMediaFileList;
  chatInputVideoFileList: ChatInputMediaFile[];
  setChatInputVideoFileList: SetChatInputMediaFileList;
  chatInputAudioFileList: ChatInputMediaFile[];
  setChatInputAudioFileList: SetChatInputMediaFileList;
  modelList: MuseModelList;
  modelListReady: boolean;
  setModelList: (modelList: MuseModelList) => void;
  reelModel: ReelModel;
  setReelModel: (reelModel: ReelModel) => void;
  youtubeUrl: ChatReelYoutubeUrl;
  setYoutubeUrl: (youtubeUrl: ChatReelYoutubeUrl) => void;
  generateVideoData: GenerateVideoData | null;
  setGenerateVideoData: (generateVideoData?: GenerateVideoData | null) => void;
  chatInputDraftResetVersion: number;
  requestChatInputDraftReset: () => void;
  skillObj: { id: string; description: string; name: string; value: string; type: string; selected?: boolean };
  setSkillObj: (skillObj: { id: string; description: string; name: string; value: string; type: string; selected?: boolean }) => void;
  storeSelectionDraft: ChatReelSelectionDraft | null | undefined;
  setStoreSelectionDraft: (storeSelectionDraft: ChatReelSelectionDraft | null | undefined) => void;
  selectionModeInfo: any;
  setSelectionModeInfo: (selectionModeInfo: any) => void;
  agentStep: number;
  setAgentStep: (agentStep: number) => void;
  hasAssistantMessage: boolean | null;
  setHasAssistantMessage: (hasAssistantMessage: boolean | null) => void;
  hasHistoryMessages: boolean;
  setHasHistoryMessages: (hasHistoryMessages: boolean) => void;
  historyMessagesLoaded: boolean;
  setHistoryMessagesLoaded: (historyMessagesLoaded: boolean) => void;
  canvasReady: boolean;
  setCanvasReady: (canvasReady: boolean) => void;

  globalConfig: any;
  setGlobalConfig: (globalConfig: any) => void;

  canvasId: string | null;
  setCanvasId: (canvasId: string | null) => void;
  historySessionChanged: boolean;
  markHistorySessionChanged: () => void;
  resetHistorySessionChanged: () => void;
};

export const useChatReelStore = create<ChatReelState>()(
  devtools(
    (set, get) => ({
      seessionId: null,
      setSeessionId: (seessionId) =>
        set((state) =>
          state.seessionId === seessionId
            ? { seessionId }
            : {
                seessionId,
                canvasId: null,
                reelModel: 'standard',
                modelListReady: false,
                chatInputFileList: [],
                chatInputVideoFileList: [],
                chatInputAudioFileList: [],
                generateVideoData: null,
                youtubeUrl: EMPTY_CHAT_REEL_YOUTUBE_URL,
                storeSelectionDraft: undefined,
                selectionModeInfo: null,
                hasHistoryMessages: false,
                historyMessagesLoaded: false,
                canvasReady: false,
                entityCount: 0,
                entityCountSource: null,
                entityModalOpen: false,
                entityModalSource: null,
                entityFormModalOpen: false,
                entityFormModalSource: null,
              },
        ),

      reelExecution: EMPTY_REEL_EXECUTION,

      beginReelExecution: (opts) => {
        const { reelExecution } = get();

        if (opts.clientRequestId) {
          if (!reelExecution.isRunning || reelExecution.clientRequestId !== opts.clientRequestId) {
            return null;
          }
          return opts.clientRequestId;
        }

        if (reelExecution.isRunning) {
          return null;
        }

        const clientRequestId = createReelExecutionRequestId();
        set(
          {
            reelExecution: {
              isRunning: true,
              source: opts.source,
              clientRequestId,
              meta: opts.meta ?? null,
            },
          },
          false,
          'reelExecution/begin',
        );
        return clientRequestId;
      },

      endReelExecution: (clientRequestId) => {
        const { reelExecution } = get();
        if (!reelExecution.isRunning) return;
        if (clientRequestId && reelExecution.clientRequestId !== clientRequestId) return;

        set({ reelExecution: EMPTY_REEL_EXECUTION }, false, 'reelExecution/end');
      },

      selectBrand: null,
      setSelectBrand: (selectBrand: BrandItem) => set({ selectBrand }),

      reelGlobaldisabled: false,
      setReelGlobaldisabled: (reelGlobaldisabled: boolean) => set({ reelGlobaldisabled }),

      entityCount: 0,
      entityCountSource: null,
      setEntityCount: (entityCount: number, source = 'session-query') => set({ entityCount, entityCountSource: source }),
      entityModalOpen: false,
      entityModalSource: null,
      setEntityModalOpen: (entityModalOpen, source) =>
        set({
          entityModalOpen,
          entityModalSource: entityModalOpen ? (source ?? 'entity') : null,
        }),
      entityFormModalOpen: false,
      entityFormModalSource: null,
      setEntityFormModalOpen: (entityFormModalOpen, source) =>
        set({
          entityFormModalOpen,
          entityFormModalSource: entityFormModalOpen ? (source ?? 'entity') : null,
        }),

      chatInputFileList: [],
      setChatInputFileList: (chatInputFileList) =>
        set((state) => {
          const nextChatInputFileList = typeof chatInputFileList === 'function' ? chatInputFileList(state.chatInputFileList) : chatInputFileList;

          return {
            chatInputFileList: nextChatInputFileList,
            // generateVideoData: nextChatInputFileList.length ? null : state.generateVideoData,
          };
        }),

      chatInputVideoFileList: [],
      setChatInputVideoFileList: (chatInputVideoFileList) =>
        set((state) => ({
          chatInputVideoFileList: typeof chatInputVideoFileList === 'function' ? chatInputVideoFileList(state.chatInputVideoFileList) : chatInputVideoFileList,
        })),

      chatInputAudioFileList: [],
      setChatInputAudioFileList: (chatInputAudioFileList) =>
        set((state) => ({
          chatInputAudioFileList: typeof chatInputAudioFileList === 'function' ? chatInputAudioFileList(state.chatInputAudioFileList) : chatInputAudioFileList,
        })),

      resetPageStatus: () =>
        set({
          seessionId: null,
          modelListReady: false,
          selectBrand: null,
          chatInputFileList: [],
          chatInputVideoFileList: [],
          chatInputAudioFileList: [],
          reelExecution: EMPTY_REEL_EXECUTION,
          reelGlobaldisabled: false,
          youtubeUrl: EMPTY_CHAT_REEL_YOUTUBE_URL,
          generateVideoData: null,
          chatInputDraftResetVersion: 0,
          storeSelectionDraft: undefined,
          selectionModeInfo: null,
          agentStep: 0,
          canvasId: null,
          hasHistoryMessages: false,
          historyMessagesLoaded: false,
          canvasReady: false,
          entityCount: 0,
          entityCountSource: null,
          entityModalOpen: false,
          entityModalSource: null,
          entityFormModalOpen: false,
          entityFormModalSource: null,
        }),

      modelList: {
        image_usages: {
          default: '',
          options: [],
        },
        aspect_ratios: {
          default: '',
          options: [],
        },
        resolutions: {
          default: '',
          options: [],
        },
        durations: {
          min: 0,
          max: 0,
          default: 0,
          step: 1,
        },
        audio: {
          default: false,
          options: [],
        },
        models: {
          default: {},
          groups: [],
        },
      },
      modelListReady: false,
      setModelList: (modelList: MuseModelList) => set({ modelList }),
      reelModel: 'standard',
      setReelModel: (reelModel: ReelModel) => set((state) => ({ reelModel, modelListReady: state.reelModel === reelModel && state.modelListReady })),

      youtubeUrl: EMPTY_CHAT_REEL_YOUTUBE_URL,
      setYoutubeUrl: (youtubeUrl: ChatReelYoutubeUrl) => set({ youtubeUrl }),

      generateVideoData: null,
      setGenerateVideoData: (generateVideoData = null) =>
        set(() => {
          if (!generateVideoData) {
            return { generateVideoData: null };
          }

          if (isOmniVideoEditAction(generateVideoData?.action)) {
            return {
              generateVideoData,
              chatInputFileList: [],
              chatInputVideoFileList: [],
              chatInputAudioFileList: [],
            };
          }

          const image = pickChatInputMediaFromGenerateVideoData(generateVideoData, 'image');
          const video = pickChatInputMediaFromGenerateVideoData(generateVideoData, 'video');
          const audio = pickChatInputMediaFromGenerateVideoData(generateVideoData, 'audio');
          return {
            generateVideoData,
            chatInputFileList: image ?? [],
            chatInputVideoFileList: video ?? [],
            chatInputAudioFileList: audio ?? [],
          };
        }),

      chatInputDraftResetVersion: 0,
      requestChatInputDraftReset: () => set((state) => ({ chatInputDraftResetVersion: state.chatInputDraftResetVersion + 1 })),

      skillObj: { id: '', description: '', name: '', value: '', type: '' },
      setSkillObj: (skillObj: { id: string; description: string; name: string; value: string; type: string; selected?: boolean }) => set({ skillObj }),

      storeSelectionDraft: undefined,
      setStoreSelectionDraft: (storeSelectionDraft) => set({ storeSelectionDraft }),

      selectionModeInfo: null,
      setSelectionModeInfo: (selectionModeInfo: any) => set({ selectionModeInfo }),
      agentStep: 0,
      setAgentStep: (agentStep: number) => set({ agentStep }),

      hasAssistantMessage: null,
      setHasAssistantMessage: (hasAssistantMessage: boolean | null) => set({ hasAssistantMessage }),
      hasHistoryMessages: false,
      setHasHistoryMessages: (hasHistoryMessages: boolean) => set({ hasHistoryMessages }),
      historyMessagesLoaded: false,
      setHistoryMessagesLoaded: (historyMessagesLoaded: boolean) => set({ historyMessagesLoaded }),
      canvasReady: false,
      setCanvasReady: (canvasReady: boolean) => set({ canvasReady }),

      globalConfig: null,
      setGlobalConfig: (globalConfig: any) => set({ globalConfig }),

      canvasId: null,
      setCanvasId: (canvasId: string | null) => set({ canvasId }),

      historySessionChanged: false, //true 表示当前会话配置已被用户手动改过，可以走自动保存；false 表示还在初始化/恢复阶段，或尚未发生用户操作
      markHistorySessionChanged: () => set({ historySessionChanged: true }), //把脏标记设为 true。只应在用户真实操作时调用
      resetHistorySessionChanged: () => set({ historySessionChanged: false }), //脏标记清回 false。用在初始化或重新拉历史时
    }),
    {
      name: 'ChatReelStore',
    },
  ),
);
